import type {
    ManifestFeatureIconButtons,
    ManifestFeatureIconButtonConfig,
    ManifestFeatureUpgradeButtonConfig,
    ManifestFeatureComponentStageGfpp
} from '@wix/document-services-types'
import {ACTIONS, type Actions, type Action} from '@wix/component-protocol/schema' // eslint-disable-line wix-editor/no-internal-import
import {transform} from 'lodash'

export interface ManifestActions {
    nativeActions: Record<string, Action>
    customActions: Record<string, Action>
}

export class ActionsBuilder {
    private readonly ACTION_BEHAVIOR = {
        HIDE: 'HIDE',
        CUSTOM_ACTION: 'CUSTOM_ACTION'
    }
    private readonly nativeActionsNames = Object.keys(ACTIONS.ACTION_NAME).filter(
        key => isNaN(Number(key)) && key !== 'UNKNOWN_ActionName' && key !== 'custom'
    )

    constructor(private readonly gfpp: ManifestFeatureComponentStageGfpp | undefined) {}

    isNativeAction(name: string): name is keyof Actions {
        return this.nativeActionsNames.includes(name)
    }

    getManifestActionItem(
        name: string,
        value: ManifestFeatureIconButtonConfig | ManifestFeatureUpgradeButtonConfig
    ): Action {
        const isCustomAction = value.behavior === this.ACTION_BEHAVIOR.CUSTOM_ACTION && 'actionId' in value
        let execution: Action['execution'] | undefined
        if (isCustomAction) {
            execution = {actionType: ACTIONS.ACTION_TYPE.event, event: {event: value.actionId}}
        }

        return {
            displayName: name,
            execution
        }
    }

    getGroupedActions(): ManifestActions {
        const iconButtons = this.gfpp?.desktop?.iconButtons ?? {}
        return transform<ManifestFeatureIconButtons, ManifestActions>(
            iconButtons,
            (acc: ManifestActions, value, key) => {
                const targetGroup = this.isNativeAction(key) ? 'nativeActions' : 'customActions'
                if (value) {
                    acc[targetGroup][key] = this.getManifestActionItem(key, value)
                }
            },
            {nativeActions: {}, customActions: {}}
        )
    }

    getNativeActions(): Record<string, Action> {
        return this.getGroupedActions().nativeActions
    }

    getCustomActions(): Record<string, Action> {
        return this.getGroupedActions().customActions
    }
}
